import React, { useMemo } from 'react';
import {
  BuildingOffice2Icon,
  ChevronRightIcon,
  ChartBarSquareIcon
} from '@heroicons/react/24/outline';
import Modal from './Modal';
import {
  PLATFORM_BANNER_METRIC_COLUMNS,
  getPlatformMetricToneClass
} from '../utils/platformBannerStats';

function tenantInitial(name) {
  const s = String(name || '').trim();
  if (!s) return '租';
  return s.slice(0, 1).toUpperCase();
}

export default function PlatformStatsBreakdownModal({
  isOpen,
  onClose,
  highlightKey,
  byTenant = [],
  loading = false,
  onSelectTenant
}) {
  const highlightCol = PLATFORM_BANNER_METRIC_COLUMNS.find((c) => c.key === highlightKey);

  const totals = useMemo(
    () =>
      PLATFORM_BANNER_METRIC_COLUMNS.reduce((acc, col) => {
        acc[col.key] = byTenant.reduce((sum, row) => sum + (Number(row[col.key]) || 0), 0);
        return acc;
      }, {}),
    [byTenant]
  );

  const highlightTotal = highlightKey ? totals[highlightKey] ?? 0 : 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="全平台数据明细">
      <div className="space-y-4">
        {/* 顶部概览 */}
        <div className="rounded-xl border border-emerald-100 bg-gradient-to-r from-emerald-50/90 via-white to-teal-50/70 px-4 py-3 dark:border-emerald-900/40 dark:from-emerald-950/30 dark:via-gray-800 dark:to-teal-950/20">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-600/10 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
              <ChartBarSquareIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                {highlightCol ? `当前指标：${highlightCol.label}` : '各租户经营数据一览'}
              </p>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                点击租户行进入「我的农场」并切换为该租户数据
              </p>
            </div>
            {!loading && byTenant.length > 0 ? (
              <div className="flex flex-wrap gap-2 shrink-0">
                {highlightCol ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-emerald-700 shadow-sm ring-1 ring-emerald-100 dark:bg-gray-900/60 dark:text-emerald-300 dark:ring-emerald-900/50">
                    <span>{highlightCol.icon}</span>
                    合计 {highlightTotal} {highlightCol.unit}
                  </span>
                ) : null}
                <span className="inline-flex items-center gap-1 rounded-full bg-white/80 px-3 py-1 text-xs text-gray-600 ring-1 ring-gray-200 dark:bg-gray-900/50 dark:text-gray-300 dark:ring-gray-700">
                  <BuildingOffice2Icon className="h-3.5 w-3.5" />
                  {byTenant.length} 家租户
                </span>
              </div>
            ) : null}
          </div>
        </div>

        {/* 数据表 */}
        <div className="overflow-hidden rounded-xl border border-gray-200/90 shadow-sm dark:border-gray-700">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50/95 dark:bg-gray-800/90">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    租户
                  </th>
                  {PLATFORM_BANNER_METRIC_COLUMNS.map((col) => {
                    const active = col.key === highlightKey;
                    return (
                      <th
                        key={col.key}
                        className={`px-3 py-3 text-right whitespace-nowrap ${
                          active
                            ? `${getPlatformMetricToneClass(col.key)} ${getPlatformMetricToneClass(col.key, 'cell')} font-semibold`
                            : 'text-gray-500 dark:text-gray-400 font-medium'
                        }`}
                      >
                        <span className="inline-flex items-center justify-end gap-1">
                          <span className="text-base leading-none">{col.icon}</span>
                          <span>{col.label}</span>
                        </span>
                      </th>
                    );
                  })}
                  <th className="w-8 px-2 py-3" aria-hidden />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/80">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center">
                      <div className="inline-flex flex-col items-center gap-3 text-gray-500">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-emerald-200 border-t-emerald-600" />
                        <span className="text-sm">正在汇总各租户数据…</span>
                      </div>
                    </td>
                  </tr>
                ) : byTenant.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center">
                      <div className="text-gray-400 dark:text-gray-500">
                        <div className="text-3xl mb-2">📊</div>
                        <p className="text-sm">暂无租户数据</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  byTenant.map((row, index) => (
                    <tr
                      key={row.tenantId}
                      className={`group cursor-pointer transition-colors ${
                        index % 2 === 0
                          ? 'bg-white dark:bg-gray-800/40'
                          : 'bg-gray-50/40 dark:bg-gray-800/20'
                      } hover:bg-emerald-50/70 dark:hover:bg-emerald-950/25`}
                      onClick={() => onSelectTenant?.(row.tenantId)}
                      title="点击进入该租户「我的农场」"
                    >
                      <td className="px-4 py-3 text-left whitespace-nowrap">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 text-xs font-bold text-white shadow-sm">
                            {tenantInitial(row.tenantName)}
                          </span>
                          <span className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[140px] sm:max-w-[200px]">
                            {row.tenantName || row.tenantId}
                          </span>
                        </div>
                      </td>
                      {PLATFORM_BANNER_METRIC_COLUMNS.map((col) => {
                        const active = col.key === highlightKey;
                        const val = Number(row[col.key]) || 0;
                        const warnHot = col.key === 'warnings' && val > 0;
                        return (
                          <td
                            key={col.key}
                            className={`px-3 py-3 text-right tabular-nums ${
                              active
                                ? `${getPlatformMetricToneClass(col.key, 'cell')} font-semibold ${getPlatformMetricToneClass(col.key)}`
                                : 'text-gray-700 dark:text-gray-200'
                            } ${warnHot && !active ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}`}
                          >
                            {val}
                          </td>
                        );
                      })}
                      <td className="px-2 py-3 text-gray-300 group-hover:text-emerald-500 transition-colors">
                        <ChevronRightIcon className="h-4 w-4" />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {!loading && byTenant.length > 0 ? (
                <tfoot>
                  <tr className="bg-gradient-to-r from-gray-50 to-emerald-50/50 dark:from-gray-800 dark:to-emerald-950/20 border-t-2 border-emerald-200/60 dark:border-emerald-800/50">
                    <td className="px-4 py-3 text-left font-bold text-gray-900 dark:text-gray-100">
                      全平台合计
                    </td>
                    {PLATFORM_BANNER_METRIC_COLUMNS.map((col) => {
                      const active = col.key === highlightKey;
                      return (
                        <td
                          key={col.key}
                          className={`px-3 py-3 text-right font-bold tabular-nums ${
                            active
                              ? `${getPlatformMetricToneClass(col.key)} ${getPlatformMetricToneClass(col.key, 'cell')}`
                              : 'text-gray-900 dark:text-gray-100'
                          }`}
                        >
                          {totals[col.key] ?? 0}
                        </td>
                      );
                    })}
                    <td />
                  </tr>
                </tfoot>
              ) : null}
            </table>
          </div>
        </div>

        <p className="text-center text-[11px] text-gray-400 dark:text-gray-500">
          数据按租户实时汇总 · 点击行可下钻查看该租户农场详情
        </p>
      </div>
    </Modal>
  );
}
